<template>
	<div>
		<b-modal ref="edit-modal" title="Edit item" :busy="loading>0">
			<b-form @submit.stop.prevent="save(); return false" id="serviceEditForm" v-if="curService">
				<b-form-group label="Protocol">
					<b-form-select v-model="curService.protocol_name" :options="protocols" value-field="name" text-field="name" required></b-form-select>
				</b-form-group>
				<b-form-group label="Service name">
					<b-form-input v-model="curService.name" placeholder="Service name" required></b-form-input>
				</b-form-group>
				<b-form-group description="Only subscribers can add channels on this service. Non-subscribers see an &quot;Upgrade&quot; badge and are blocked from joining.">
					<b-form-checkbox v-model="curService.flags" value="require-subscription">Require subscription</b-form-checkbox>
				</b-form-group>
				<b-form-group label="URL">
					<b-form-input v-model="curService.url" placeholder="Service URL"></b-form-input>
				</b-form-group>
				<b-form-group label="Icon">
					<pipi-pupu-file ref="fileUpload" v-model="curService.icon" accept="image/*"></pipi-pupu-file>
				</b-form-group>
				<b-form-group label="Capabilities">
					<b-form-checkbox-group v-model="curService.capabilities" :options="capabilityTypes" :required="curService.capabilities && curService.capabilities.length === 0"></b-form-checkbox-group>
				</b-form-group>
				<b-form-group v-for="(field, i) in fields" :key="i" :label="field.type != 'bool' ? field.label||i : null" :description="field.description">
					<b-form-input v-if="field.type == 'int' || field.type == 'float'" v-model.number="curService.settings[i]" type="number"></b-form-input>
					<password-input v-else-if="field.type == 'password'" v-model="curService.settings[i]"></password-input>
					<b-form-input v-else-if="field.type == 'color' || field.type == 'email'" v-model="curService.settings[i]" :type="field.type"></b-form-input>
					<b-form-checkbox v-else-if="field.type == 'bool'" v-model="curService.settings[i]" :value="true" :unchecked-value="false">{{field.label||i}}</b-form-checkbox>
					<b-form-textarea v-else-if="field.type == 'textarea'" v-model="curService.settings[i]"></b-form-textarea>
					<b-form-input v-else v-model="curService.settings[i]"></b-form-input>
				</b-form-group>
				Service rate limiting:
				<b-form-row>
					<b-col>
						<b-form-group label="Token bucket size">
							<b-form-input type="number" v-model.number="curService.settings.bucketSize" />
						</b-form-group>
					</b-col>
					<b-col>
						<b-form-group label="Message limit">
							<b-form-input type="number" v-model.number="curService.settings.messageLimit" />
						</b-form-group>
					</b-col>
					<b-col>
						<b-form-group label="Message interval">
							<b-form-input type="number" v-model.number="curService.settings.messageInterval" />
						</b-form-group>
					</b-col>
				</b-form-row>
				Channel rate limiting:
				<b-form-row>
					<b-col>
						<b-form-group label="Token Bucket Size">
							<b-form-input type="number" v-model.number="curService.settings.channelMessageBucketSize" />
						</b-form-group>
					</b-col>
					<b-col>
						<b-form-group label="Message limit">
							<b-form-input type="number" v-model.number="curService.settings.channelMessageLimit" />
						</b-form-group>
					</b-col>
					<b-col>
						<b-form-group label="Message interval">
							<b-form-input type="number" v-model.number="curService.settings.channelMessageInterval" />
						</b-form-group>
					</b-col>
				</b-form-row>
				<b-form-group label="Message queue size">
					<b-form-input type="number" v-model.number="curService.settings.channelMessageQueueSize" />
				</b-form-group>
				<b-form-group label="Session">
					<b-form-textarea v-model="curService.session" placeholder="JSON"></b-form-textarea>
				</b-form-group>
			</b-form>
			<b-alert variant="danger" :show="!!error">{{error}}</b-alert>
			<template #modal-footer="{cancel}">
				<b-spinner v-show="loading>0" small label="Loading"></b-spinner>
				<b-button size="sm" variant="success" type="submit" form="serviceEditForm">
					Save
				</b-button>
				<b-button size="sm" @click="cancel()">
					Cancel
				</b-button>
			</template>
		</b-modal>
	</div>
</template>

<script>
import PasswordInput from '../../PasswordInput.vue';
import PipiPupuFile from '../../PipiPupuFile.vue';

export default {
	name: 'ServiceEditModal',
	components: {
		PasswordInput,
		PipiPupuFile,
	},
	data() { return {
		protocols: [],
		fields: null,
		id: null,
		curService: {settings: {}},
		loading: 0,
		error: null,
		capabilityTypes: [ 'chat', 'restreaming', 'calls' ],
	}},
	watch: {
		'curService.protocol_name': function() {
			if(this)
				this.loadFields();
		}
	},
	methods: {
		load(promise) {
			this.loading++;
			promise.catch(err => {
				this.error = err&&err.error||err.toString();
			}).finally(() => {
				this.loading--;
			});
			return promise;
		},
		loadProtocols() {
			this.load(this.$api.get('admin/protocols')).then(protocols => this.protocols = protocols);
		},
		loadFields() {
			if(!this.curService.protocol_name) {
				this.fields = null;
				return;
			}

			this.load(this.$api.get(`admin/protocol/${encodeURIComponent(this.curService.protocol_name)}`)).then(proto => this.fields = proto?.serviceSettingFields);
		},
		show(id) {
			this.curService = {settings: {}};
			this.loadProtocols();
			this.error = null;
			if(id) {
				this.$set(this, 'id', id);
				this.load(this.$api.get(`admin/service/${encodeURIComponent(id)}`)).then(service => {
					service.settings ||= {};
					service.session = service.session && JSON.stringify(service.session);
					service.flags = service.flags || [];
					this.curService = service
				});
			} else {
				this.$set(this, 'id', null);
				this.curService = {settings:{}, capabilities:[], flags:[]};
			}

			this.$refs['edit-modal'].show();
		},
		async save() {
			try {
				this.error = null;
				this.loading++;
				await this.$refs.fileUpload?.waitForUploads();
				const data = {
					protocolName: this.curService.protocol_name,
					name: this.curService.name,
					flags: this.curService.flags || [],
					url: this.curService.url,
					settings: this.curService.settings,
					session: this.curService.session,
					capabilities: this.curService.capabilities,
					iconMd5: this.curService.icon&&this.curService.icon.md5,
				};
				if(this.id)
					await this.$api.post(`admin/service/${encodeURIComponent(this.id)}`, data);
				else
					await this.$api.post('admin/service', data);
				this.$refs['edit-modal'].hide();
				this.$bvToast.toast('Successfully saved', {
					title: 'Success',
					variant: 'success',
					solid: true
				});
				this.$emit('saved');
			} catch(e) {
				console.error(e);
				this.error = e;
			} finally {
				this.loading--;
			}
		},
	}
};
</script>
